/**
 * Static MarketHeader for SSR – no hooks, no JavaScript.
 * Renders the same hero + stats when a location filter is set. "Remove location" is a link.
 */
import React from 'react';
import { Wallet, Laptop, Translate } from '@phosphor-icons/react';
import { Link } from './Link';
import { getLocationBackgroundImage } from '../utils/location/get-location-background-image-url';
import type { LocationTrendsData } from '../hooks/useLocationTrends';
import type { MarketHeaderStaticData } from '../features/srp/SrpViewModel';
import type { SrpStaticStrings } from '../features/srp/SrpViewModel';
import { formatLocationBackgroundImageAltSync } from '../utils/location/format-location-background-image-alt';
import { buildLocationSalaryPercentileSeoData } from '../utils/location/build-location-salary-percentile-seo';

/** Literal Tailwind class strings for market header (no dynamic concatenation). */
const MARKET_HEADER_CARD_WITH_BG = 'bg-white/90 dark:bg-[#1F1509]/92 border border-white/40 dark:border-[#4A3520]/60';
const MARKET_HEADER_CARD_NO_BG =
  'bg-white/85 dark:bg-[#1F1509]/95 shadow-lg dark:shadow-none border border-[#DDD5C8]/70 dark:border-[#4A3520]/85';
const MARKET_HEADER_TITLE_WITH_BG = 'text-white';
const MARKET_HEADER_TITLE_NO_BG = 'text-[#1E1A14] dark:text-[#F2EADF]';
const MARKET_HEADER_SUBTEXT_WITH_BG = 'text-white/80';
const MARKET_HEADER_SUBTEXT_NO_BG = 'text-[#3D3529] dark:text-[#C4A882]';
const MARKET_HEADER_OVERVIEW_WITH_BG = 'text-white/90';
const MARKET_HEADER_OVERVIEW_NO_BG = 'text-[#3D3529] dark:text-[#C4A882]';

function workModelHeadline(
  onsite: number,
  hybrid: number,
  remote: number
): { headline: string; onsitePercent: number; hybridPercent: number; remotePercent: number } | null {
  const total = onsite + hybrid + remote;
  if (total === 0) return null;
  const onsitePercent = (onsite / total) * 100;
  const hybridPercent = (hybrid / total) * 100;
  const remotePercent = (remote / total) * 100;
  let headline: string;
  if (onsitePercent > 60) headline = 'Primarily Onsite';
  else if (hybridPercent > 40 || (hybridPercent > onsitePercent && hybridPercent > remotePercent)) headline = 'Hybrid Popular';
  else if (remotePercent > 30 || (remotePercent > onsitePercent && remotePercent > hybridPercent)) headline = 'Remote-Friendly';
  else if (hybridPercent + remotePercent > 60) headline = 'Flexible Work';
  else if (onsitePercent > hybridPercent && onsitePercent > remotePercent) headline = 'Primarily Onsite';
  else if (hybridPercent > remotePercent) headline = 'Hybrid Popular';
  else headline = 'Remote-Friendly';
  return {
    headline,
    onsitePercent: Math.round(onsitePercent),
    hybridPercent: Math.round(hybridPercent),
    remotePercent: Math.round(remotePercent),
  };
}

function WorkModelBarStatic({
  onsite,
  hybrid,
  remote,
}: {
  onsite: number;
  hybrid: number;
  remote: number;
}): React.ReactElement | null {
  const total = onsite + hybrid + remote;
  if (total === 0) return null;
  const onsitePercent = (onsite / total) * 100;
  const hybridPercent = (hybrid / total) * 100;
  const remotePercent = (remote / total) * 100;
  return (
    <div className="w-full h-3 bg-[#DDD5C8] dark:bg-[#4A3520] rounded-full overflow-hidden flex" aria-hidden>
      {onsitePercent > 0 && (
        <div className="bg-[#3B5E45] dark:bg-[#5A8A68]" style={{ width: `${onsitePercent}%` }} />
      )}
      {hybridPercent > 0 && (
        <div className="bg-[#5A8A68] dark:bg-[#98CFA8]" style={{ width: `${hybridPercent}%` }} />
      )}
      {remotePercent > 0 && (
        <div className="bg-[#90BC9C] dark:bg-[#B6D4BC]" style={{ width: `${remotePercent}%` }} />
      )}
    </div>
  );
}

function LanguageBarStatic({
  english,
  local,
}: {
  english: number;
  local: number;
}): React.ReactElement | null {
  const total = english + local;
  if (total === 0) return null;
  const englishPercent = (english / total) * 100;
  const localPercent = (local / total) * 100;
  return (
    <div className="w-full h-3 bg-[#DDD5C8] dark:bg-[#4A3520] rounded-full overflow-hidden flex" aria-hidden>
      {englishPercent > 0 && (
        <div className="bg-[#3B5E45] dark:bg-[#98CFA8]" style={{ width: `${englishPercent}%` }} />
      )}
      {localPercent > 0 && (
        <div className="bg-[#C8DDCE] dark:bg-[#1A2E22]" style={{ width: `${localPercent}%` }} />
      )}
    </div>
  );
}

export interface MarketHeaderStaticProps {
  locale: string;
  location: string;
  locationTrendsData?: LocationTrendsData | null;
  marketHeaderStatic: MarketHeaderStaticData;
  strings: NonNullable<SrpStaticStrings['marketHeader']>;
}

export function MarketHeaderStatic({
  locale,
  location,
  locationTrendsData,
  marketHeaderStatic,
  strings,
}: MarketHeaderStaticProps): React.ReactElement {
  const bgImage = getLocationBackgroundImage(location);
  const isShowingBackgroundImage = Boolean(bgImage);

  const salaryStats = locationTrendsData?.salaryStats?.aggregate;
  const remotenessDistribution = locationTrendsData?.trends?.remotenessDistribution ?? {};
  const languageRequirements = locationTrendsData?.trends?.languageRequirements ?? {};
  const onsite = remotenessDistribution.onsite ?? 0;
  const hybrid = remotenessDistribution.hybrid ?? 0;
  const remote = remotenessDistribution.remote ?? 0;
  const workModel = workModelHeadline(onsite, hybrid, remote);
  const english = languageRequirements['English'] ?? 0;
  const finnish = languageRequirements['Finnish'] ?? languageRequirements['Suomi'] ?? 0;
  const swedish = languageRequirements['Swedish'] ?? languageRequirements['Svenska'] ?? 0;
  const local = finnish + swedish;
  const languageTotal = english + local;

  const salaryValue = salaryStats
    ? (() => {
        const avg = salaryStats.average?.min ?? salaryStats.median?.min ?? 0;
        return avg > 0 ? `€${Math.round(avg / 1000)}k/mo` : null;
      })()
    : null;
  const salaryRange = salaryStats
    ? (() => {
        const min = salaryStats.min ?? 0;
        const max = salaryStats.max ?? 0;
        const p25 = salaryStats.percentile25?.min ?? 0;
        const p75 = salaryStats.percentile75?.min ?? 0;
        if (min === 0 && max === 0) return null;
        if (p25 > 0 && p75 > 0) {
          return { low: p25, high: p75, isPercentile: true };
        }
        return { low: min, high: max, isPercentile: false };
      })()
    : null;
  const salaryPercentileSeoData = buildLocationSalaryPercentileSeoData(salaryStats);

  const cardClass = isShowingBackgroundImage ? MARKET_HEADER_CARD_WITH_BG : MARKET_HEADER_CARD_NO_BG;
  const textClass = isShowingBackgroundImage ? MARKET_HEADER_TITLE_WITH_BG : MARKET_HEADER_TITLE_NO_BG;
  const subtextClass = isShowingBackgroundImage ? MARKET_HEADER_SUBTEXT_WITH_BG : MARKET_HEADER_SUBTEXT_NO_BG;

  const backgroundImageAlt =
    strings.backgroundImageAlt ??
    formatLocationBackgroundImageAltSync(locale, marketHeaderStatic.translatedLocation);

  return (
    <section
      id="location-info-header"
      className="location-info-header market-header relative overflow-hidden"
      aria-labelledby="location-header-title"
    >
      {isShowingBackgroundImage && bgImage ? (
        <div className="absolute inset-0 rounded-b-2xl sm:rounded-b-3xl overflow-hidden">
          <img
            src={bgImage.src}
            srcSet={bgImage.srcset || undefined}
            sizes="100vw"
            alt={backgroundImageAlt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/30" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#C8DDCE]/50 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2E22] rounded-b-2xl sm:rounded-b-3xl" />
      )}

      <div className="relative z-10 p-6 sm:p-8 lg:p-12">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <h1
              id="location-header-title"
              className={`text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight ${textClass}`}
            >
              {marketHeaderStatic.title}
            </h1>
            <p className={`text-sm sm:text-base mt-2 ${subtextClass}`}>
              {strings.searchResultSubtitle}
            </p>
          </div>
          <Link
            href={marketHeaderStatic.removeLocationUrl}
            className={`flex-shrink-0 px-3 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer bg-white/90 dark:bg-[#2A1D0F]/95 text-[#3D3529] dark:text-[#C4A882] border border-[#DDD5C8]/80 dark:border-[#4A3520] hover:bg-white dark:hover:bg-[#342415] shadow-sm dark:shadow-none`}
            aria-label={strings.removeLocation}
          >
            {strings.removeLocation}
          </Link>
        </div>

        {marketHeaderStatic.overviewText ? (
          <div className={`text-base sm:text-lg leading-relaxed max-w-5xl lg:max-w-6xl mb-6 ${isShowingBackgroundImage ? MARKET_HEADER_OVERVIEW_WITH_BG : MARKET_HEADER_OVERVIEW_NO_BG}`}>
            <p>{marketHeaderStatic.overviewText}</p>
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#F5EDD0] dark:bg-[#2A1E08] flex items-center justify-center">
                <Wallet size={20} className="text-[#6E4F00] dark:text-[#D4A820]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">{strings.salaryLabel}</div>
                {salaryValue && (
                  <div className="text-2xl font-bold text-[#1E1A14] dark:text-[#F2EADF]">{salaryValue}</div>
                )}
              </div>
            </div>
            {salaryRange && (
              <div className="mt-4">
                <div className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] mb-1">
                  {salaryRange.isPercentile ? strings.salaryRangePercentile : strings.salaryRange}
                </div>
                <div className="text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                  €{Math.round(salaryRange.low / 1000)}k - €{Math.round(salaryRange.high / 1000)}k/mo
                </div>
              </div>
            )}
            {marketHeaderStatic.salaryBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {marketHeaderStatic.salaryBlurb}
              </p>
            )}
          </div>

          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] flex items-center justify-center">
                <Laptop size={20} className="text-[#3B5E45] dark:text-[#98CFA8]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">{strings.workModelLabel}</div>
                {workModel ? (
                  <>
                    <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">{workModel.headline}</div>
                    <div className="text-xs font-normal text-[#7A6E5F] dark:text-[#B8A48C] mt-0.5">
                      {strings.workModelOnsite.replace('{percent}', String(workModel.onsitePercent))} •{' '}
                      {strings.workModelHybrid.replace('{percent}', String(workModel.hybridPercent))} •{' '}
                      {strings.workModelRemote.replace('{percent}', String(workModel.remotePercent))}
                    </div>
                  </>
                ) : (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">{strings.workModelNoData}</div>
                )}
              </div>
            </div>
            {onsite + hybrid + remote > 0 && (
              <figure className="mt-4">
                <WorkModelBarStatic onsite={onsite} hybrid={hybrid} remote={remote} />
              </figure>
            )}
            {marketHeaderStatic.workModelBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {marketHeaderStatic.workModelBlurb}
              </p>
            )}
          </div>

          <div className={`rounded-[2.5rem] p-6 ${cardClass}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-[#DDD5C8] dark:bg-[#2A1D0F] flex items-center justify-center">
                <Translate size={20} className="text-[#5A3E20] dark:text-[#D4B898]" />
              </div>
              <div>
                <div className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882]">{strings.languageLabel}</div>
                {languageTotal > 0 ? (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
                    {Math.round((english / languageTotal) * 100)}% English
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF]">Mixed</div>
                )}
              </div>
            </div>
            {languageTotal > 0 && (
              <figure className="mt-4">
                <LanguageBarStatic english={english} local={local} />
              </figure>
            )}
            {marketHeaderStatic.languageBlurb && (
              <p className="mt-3 text-xs sm:text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882]">
                {marketHeaderStatic.languageBlurb}
              </p>
            )}
          </div>
        </div>

        <div className="sr-only">
          <table>
            <caption>
              Advertised salary distribution in {marketHeaderStatic.translatedLocation.split(',')[0]?.trim() || marketHeaderStatic.translatedLocation}, Finland — full-time roles
            </caption>
            <thead>
              <tr>
                <th scope="col">Percentile</th>
                <th scope="col">Monthly salary (EUR)</th>
              </tr>
            </thead>
            {salaryPercentileSeoData ? (
              <tbody>
                <tr>
                  <td>25th percentile</td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile25).toLocaleString()}/month</td>
                </tr>
                <tr>
                  <td>Median (50th percentile)</td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile50).toLocaleString()}/month</td>
                </tr>
                <tr>
                  <td>75th percentile</td>
                  <td>€{Math.round(salaryPercentileSeoData.percentile75).toLocaleString()}/month</td>
                </tr>
              </tbody>
            ) : null}
          </table>
          {salaryPercentileSeoData?.sampleSize ? (
            <p>
              Based on {salaryPercentileSeoData.sampleSize.toLocaleString()} job postings with explicit salary data in {marketHeaderStatic.translatedLocation.split(',')[0]?.trim() || marketHeaderStatic.translatedLocation}, Finland.
            </p>
          ) : null}
          {workModel ? (
            <table>
              <caption>
                Work model distribution in {marketHeaderStatic.translatedLocation}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Work model</th>
                  <th scope="col">Share of listings</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Onsite</td>
                  <td>{workModel.onsitePercent}%</td>
                </tr>
                <tr>
                  <td>Hybrid</td>
                  <td>{workModel.hybridPercent}%</td>
                </tr>
                <tr>
                  <td>Remote</td>
                  <td>{workModel.remotePercent}%</td>
                </tr>
              </tbody>
            </table>
          ) : null}
          {languageTotal > 0 ? (
            <table>
              <caption>Language demand in {marketHeaderStatic.translatedLocation}</caption>
              <thead>
                <tr>
                  <th scope="col">Language category</th>
                  <th scope="col">Share of listings</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>English</td>
                  <td>{Math.round((english / languageTotal) * 100)}%</td>
                </tr>
                <tr>
                  <td>Local languages</td>
                  <td>{Math.round((local / languageTotal) * 100)}%</td>
                </tr>
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </section>
  );
}
